<template>
  <v-container fluid>
    <v-row>
      <!-- Invoice Summary Cards -->
      <v-col cols="12">
        <v-row dense>
          <v-col cols="6" sm="6" md="3">
            <v-card>
              <v-card-text>
                <div class="caption grey--text text--darken-1">
                  Rechnungen Gesamt
                </div>
                <div class="text-h5 font-weight-medium primary--text">
                  {{ invoices.length }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="6" md="3">
            <v-card>
              <v-card-text>
                <div class="caption grey--text text--darken-1">
                  Kundenguthaben
                </div>
                <div class="text-h6 font-weight-medium success--text">
                  {{ invoiceTotalAmount }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="6" md="3">
            <v-card>
              <v-card-text>
                <div class="caption grey--text text--darken-1">
                  Offene Beträge
                </div>
                <div class="text-h6 font-weight-medium warning--text">
                  {{ invoiceOpenAmount }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="6" md="3">
            <v-card>
              <v-card-text>
                <div class="caption grey--text text--darken-1">
                  Abgerechnete Lizenzen
                </div>
                <div class="text-h5 font-weight-medium info--text">
                  {{
                    invoices.reduce(
                      (sum, inv) => sum + (inv.totalLicenses || 0),
                      0,
                    )
                  }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- Invoices Table -->
      <v-col cols="12">
        <v-card>
          <v-data-table
            :items="filteredInvoices"
            :headers="invoicesHeaders"
            item-key="id"
            :mobile-breakpoint="0"
            :items-per-page="15"
            :sort-by="['created']"
            :sort-desc="[true]"
            :footer-props="{
              'items-per-page-options': [15, 25, 50, -1],
            }"
          >
            <template v-slot:top>
              <slot name="top">
                <v-card flat>
                  <v-card-title class="body-2 v-card__subtitle">
                    <v-icon left small class="mr-2"
                      >mdi-invoice-text-multiple</v-icon
                    >Rechnungen
                    <v-spacer></v-spacer>
                    <v-chip small outlined class="ml-2">
                      {{ filteredInvoices.length }} / {{ invoices.length }}
                    </v-chip>
                  </v-card-title>
                  <v-card-text>
                    <v-row dense>
                      <v-col cols="6" sm="4" md="3">
                        <v-text-field
                          v-model="invoicesSearch"
                          outlined
                          dense
                          clearable
                          hide-details="auto"
                          append-icon="mdi-magnify"
                          label="Suchen"
                          persistent-hint
                        ></v-text-field>
                      </v-col>
                      <v-spacer></v-spacer>
                      <v-col cols="6" sm="6" md="3">
                        <v-select
                          v-model="invoiceStatusFilter"
                          outlined
                          dense
                          clearable
                          multiple
                          :items="invoiceStatusOptions"
                          label="Status filtern"
                          prepend-inner-icon="mdi-filter-variant"
                          hide-details="auto"
                        >
                          <template v-slot:selection="{ item, index }">
                            <invoice-status-chip
                              v-if="index < 2"
                              :status="item.value"
                              small
                            >
                            </invoice-status-chip>
                            <span
                              v-if="index === 2"
                              class="grey--text text-caption"
                            >
                              (+{{ invoiceStatusFilter.length - 2 }} weitere)
                            </span>
                          </template>
                        </v-select>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </slot>
            </template>
            <template v-slot:[`item.number`]="{ item }">
              <div
                class="font-weight-medium primary--text"
                style="cursor: pointer"
                @click="showInvoiceDetails(item)"
              >
                {{ item.number || "Entwurf" }}
              </div>
              <div class="caption grey--text" v-if="checkIfSupport">
                {{ item.id }}
              </div>
            </template>
            <template v-slot:[`item.created`]="{ item }">
              <div>{{ timestampConverter(item.created) }}</div>
              <div
                class="caption grey--text"
                :class="{
                  'red--text':
                    item.dueDate &&
                    item.dueDate < Date.now() / 1000 &&
                    item.status === 'open',
                }"
                v-if="item.dueDate"
              >
                Fällig: {{ timestampConverter(item.dueDate) }}
              </div>
            </template>
            <template v-slot:[`item.period`]="{ item }">
              <div
                v-if="item.periodStart && item.periodEnd"
                class="text-no-wrap"
              >
                {{ timestampConverter(item.periodStart) }}
                <v-icon x-small>mdi-arrow-right</v-icon>
                {{ timestampConverter(item.periodEnd) }}
              </div>
              <div v-else class="grey--text caption">-</div>
            </template>
            <template v-slot:[`item.total`]="{ item }">
              <div class="font-weight-bold text-no-wrap">
                {{ getAmountFromCents(item.total, item.currency) }}
              </div>
              <div
                v-if="
                  item.amountRemaining > 0 &&
                  item.amountRemaining !== item.total
                "
                class="caption error--text"
              >
                Offen:
                {{ getAmountFromCents(item.amountRemaining, item.currency) }}
              </div>
            </template>
            <template v-slot:[`item.totalLicenses`]="{ item }">
              <v-btn
                v-if="item.totalLicenses"
                small
                outlined
                text
                rounded
                depressed
                color="primary"
                @click="showInvoiceLicenseDetails(item)"
              >
                {{ item.totalLicenses }}
                <v-icon x-small class="ml-2">mdi-open-in-new</v-icon>
              </v-btn>

              <span v-else class="grey--text caption">-</span>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <invoice-status-chip :status="item.status" small>
              </invoice-status-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom v-if="item.hostedInvoiceUrl">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="showInvoiceDetails(item)"
                    ><v-icon small>mdi-open-in-new</v-icon></v-btn
                  >
                </template>
                <span>Rechnung öffnen</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.invoicePdf">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openInvoice(item.invoicePdf)"
                    ><v-icon small>mdi-file-pdf-box</v-icon></v-btn
                  >
                </template>
                <span>PDF herunterladen</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Invoice License Details Dialog -->
    <v-dialog
      v-model="licenseDetailsDialog.model"
      max-width="1300px"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-invoice-text</v-icon>
          <span
            >Lizenzdetails für Rechnung
            <code>{{
              licenseDetailsDialog.invoice?.number || "Rechnung"
            }}</code></span
          >
          <v-spacer></v-spacer>
          <v-btn icon @click="licenseDetailsDialog.model = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="licenseDetailsDialog.invoice">
          <!-- Invoice Summary -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="font-weight-medium">
              <v-icon left small>mdi-information</v-icon>
              Rechnungsübersicht
            </v-card-subtitle>
            <v-card-text>
              <v-row dense>
                <v-col cols="4">
                  <div class="caption grey--text">Rechnungsnummer</div>
                  <div class="font-weight-medium">
                    <code>{{
                      licenseDetailsDialog.invoice.number ||
                      "Noch nicht vergeben"
                    }}</code>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="caption grey--text">Gesamtbetrag</div>
                  <div class="font-weight-medium">
                    {{
                      getAmountFromCents(
                        licenseDetailsDialog.invoice.total,
                        licenseDetailsDialog.invoice.currency,
                      )
                    }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="caption grey--text">Abgerechnete Lizenzen</div>
                  <div class="font-weight-medium">
                    {{ licenseDetailsDialog.invoice.totalLicenses || 0 }}
                    Lizenzen
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Detailed License List -->
          <v-card outlined v-if="licenseDetailsDialog.invoice.billedLicenses">
            <v-card-subtitle class="font-weight-medium">
              <v-icon left small>mdi-account-details</v-icon>
              Lizenzverteilung in Rechnung
            </v-card-subtitle>

            <v-data-table
              :items="licenseBillingEntriesWithKeys"
              :headers="licenseDetailsHeaders"
              :items-per-page="10"
              class="elevation-0"
              item-key="uniqueKey"
            >
              <template v-slot:[`item.userName`]="{ item }">
                <div class="font-weight-medium">{{ item.userName }}</div>
                <div class="caption grey--text">
                  {{ item.userEmail || item.userId }}
                </div>
              </template>

              <template v-slot:[`item.licenseType`]="{ item }">
                <v-chip
                  small
                  :color="getLicenseTypeColor(item.licenseType)"
                  outlined
                >
                  {{ item.licenseType }}
                </v-chip>
              </template>

              <template v-slot:[`item.prorationDetails`]="{ item }">
                <div v-if="item.sharedQuantity && item.sharedQuantity > 1">
                  <div class="font-weight-medium warning--text">
                    <v-icon x-small color="warning" class="mr-1"
                      >mdi-clock-outline</v-icon
                    >
                    Anteilig
                  </div>
                  <div class="caption grey--text">
                    1 von {{ item.sharedQuantity }} Nutzern
                  </div>
                </div>
                <div
                  v-else-if="item.prorationDetails?.prorationFactor < 1"
                  class="font-weight-medium warning--text"
                >
                  <v-icon x-small color="warning" class="mr-1"
                    >mdi-clock-outline</v-icon
                  >
                  Anteilig
                </div>
                <div v-else class="success--text font-weight-medium">
                  <v-icon x-small color="success" class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  Vollzeitraum
                </div>
              </template>

              <template v-slot:[`item.billedAmount`]="{ item }">
                <div class="font-weight-medium">
                  {{ getAmountFromCents(item.billedAmount, item.currency) }}
                </div>
                <div
                  v-if="item.sharedQuantity && item.sharedQuantity > 1"
                  class="caption grey--text"
                >
                  von {{ getAmountFromCents(item.unitAmount, item.currency) }}
                </div>
                <div
                  v-else-if="item.prorationDetails?.prorationFactor < 1"
                  class="caption grey--text"
                >
                  von {{ getAmountFromCents(item.unitAmount, item.currency) }}
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="licenseDetailsDialog.model = false"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Invoice Details Dialog -->
    <v-dialog
      v-model="invoiceDetailsDialog.model"
      max-width="1300px"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-receipt-text</v-icon>
          <span>
            Rechnungsdetails
            {{
              invoiceDetailsDialog.invoice?.number
                ? `- ${invoiceDetailsDialog.invoice.number}`
                : ""
            }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="invoiceDetailsDialog.model = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="invoiceDetailsDialog.invoice">
          <!-- Invoice Header Info -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="font-weight-medium">
              <v-icon left small>mdi-information</v-icon>
              Rechnungsinformationen
            </v-card-subtitle>
            <v-card-text>
              <v-row dense>
                <v-col cols="6" md="3">
                  <div class="caption grey--text">Rechnungsnummer</div>
                  <div class="font-weight-medium">
                    <code>{{
                      invoiceDetailsDialog.invoice.number || "Entwurf"
                    }}</code>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="caption grey--text">Status</div>
                  <div>
                    <invoice-status-chip
                      :status="invoiceDetailsDialog.invoice.status"
                      small
                    />
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="caption grey--text">Erstellt am</div>
                  <div class="font-weight-medium">
                    {{
                      timestampConverter(invoiceDetailsDialog.invoice.created)
                    }}
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="caption grey--text">Fällig am</div>
                  <div class="font-weight-medium">
                    {{
                      invoiceDetailsDialog.invoice.dueDate
                        ? timestampConverter(
                            invoiceDetailsDialog.invoice.dueDate,
                          )
                        : "-"
                    }}
                  </div>
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                  v-if="invoiceDetailsDialog.invoice.periodStart"
                >
                  <div class="caption grey--text">Abrechnungszeitraum</div>
                  <div class="font-weight-medium">
                    {{
                      timestampConverter(
                        invoiceDetailsDialog.invoice.periodStart,
                      )
                    }}
                  </div>
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                  v-if="invoiceDetailsDialog.invoice.periodEnd"
                >
                  <div class="caption grey--text">Bis</div>
                  <div class="font-weight-medium">
                    {{
                      timestampConverter(invoiceDetailsDialog.invoice.periodEnd)
                    }}
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="caption grey--text">Gesamtbetrag</div>
                  <div class="font-weight-medium primary--text">
                    {{
                      getAmountFromCents(
                        invoiceDetailsDialog.invoice.total,
                        invoiceDetailsDialog.invoice.currency,
                      )
                    }}
                  </div>
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                  v-if="invoiceDetailsDialog.invoice.amountPaid > 0"
                >
                  <div class="caption grey--text">Bezahlt</div>
                  <div class="font-weight-medium success--text">
                    {{
                      getAmountFromCents(
                        invoiceDetailsDialog.invoice.amountPaid,
                        invoiceDetailsDialog.invoice.currency,
                      )
                    }}
                  </div>
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                  v-if="invoiceDetailsDialog.invoice.amountRemaining > 0"
                >
                  <div class="caption grey--text">Offen</div>
                  <div class="font-weight-medium error--text">
                    {{
                      getAmountFromCents(
                        invoiceDetailsDialog.invoice.amountRemaining,
                        invoiceDetailsDialog.invoice.currency,
                      )
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Line Items -->
          <v-card
            outlined
            class="mb-4"
            v-if="
              invoiceDetailsDialog.invoice.lines &&
              invoiceDetailsDialog.invoice.lines.length > 0
            "
          >
            <v-card-subtitle class="font-weight-medium">
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              Rechnungspositionen (Übersicht)
            </v-card-subtitle>
            <v-card-subtitle class="py-0 font-italic">
              Maßgeblich sind die auf der Rechnung angegebenen Positionen,
              insbes. steuerlichen Angaben und Ausweise.
            </v-card-subtitle>

            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Beschreibung</th>
                    <th class="text-center">Menge</th>
                    <th class="text-right">Einzelpreis</th>
                    <th class="text-right">Summe</th>
                    <th class="text-center" style="width: 80px">Lizenzen</th>
                  </tr>
                </thead>
                <tbody
                  v-for="(group, groupIndex) in groupedInvoiceLines"
                  :key="'group-' + groupIndex"
                >
                  <!-- Group Header -->
                  <tr class="grey lighten-4">
                    <td colspan="3" class="font-weight-bold py-2">
                      {{ group.type }}
                    </td>
                    <td class="text-right font-weight-bold py-2">
                      {{
                        getAmountFromCents(
                          group.subtotal,
                          invoiceDetailsDialog.invoice.currency,
                        )
                      }}
                    </td>
                    <td></td>
                  </tr>
                  <!-- Group Line Items -->
                  <tr
                    v-for="line in group.lines"
                    :key="'line-' + line.originalIndex"
                  >
                    <td class="py-2 pl-6">
                      <div class="font-weight-medium">
                        {{ line.product?.name || "Keine Beschreibung" }}
                        <span
                          v-if="line.product?.unitLabel"
                          class="grey--text font-weight-regular"
                          >(je {{ line.product.unitLabel }})</span
                        >
                      </div>
                      <!-- <div v-if="line.description" class="caption grey--text">
                        {{ line.description }}
                      </div> -->
                      <div class="caption grey--text" v-if="line.period">
                        {{
                          new Date(line.period.start * 1000).toLocaleDateString(
                            "de-DE",
                          )
                        }}
                        -
                        {{
                          new Date(line.period.end * 1000).toLocaleDateString(
                            "de-DE",
                          )
                        }}
                      </div>
                      <div class="caption grey--text" v-if="line.proration">
                        <v-icon x-small color="warning"
                          >mdi-clock-outline</v-icon
                        >
                        Anteilig
                      </div>
                    </td>
                    <td class="text-center">{{ line.quantity || 1 }}</td>
                    <td class="text-right">
                      {{
                        getAmountFromCents(
                          line.amount / (line.quantity || 1),
                          line.currency ||
                            invoiceDetailsDialog.invoice.currency,
                        )
                      }}
                    </td>
                    <td class="text-right font-weight-medium">
                      {{
                        getAmountFromCents(
                          line.amount,
                          line.currency ||
                            invoiceDetailsDialog.invoice.currency,
                        )
                      }}
                    </td>
                    <td class="text-center">
                      <v-btn
                        v-if="getLineItemLicenseCount(line) > 0"
                        small
                        outlined
                        text
                        rounded
                        depressed
                        color="primary"
                        @click="showLineItemLicenses(line)"
                      >
                        ({{ getLineItemLicenseCount(line) }})
                        <v-icon small right>mdi-open-in-new</v-icon>
                      </v-btn>
                      <span v-else class="grey--text caption">-</span>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3" class="text-right font-weight-bold">
                      Zwischensumme
                    </td>
                    <td class="text-right font-weight-bold">
                      {{
                        getAmountFromCents(
                          invoiceDetailsDialog.invoice.subtotal ||
                            invoiceDetailsDialog.invoice.total,
                          invoiceDetailsDialog.invoice.currency,
                        )
                      }}
                    </td>
                    <td></td>
                  </tr>
                  <tr v-if="invoiceDetailsDialog.invoice.tax">
                    <td colspan="3" class="text-right">Steuern gesamt</td>
                    <td class="text-right">
                      {{
                        getAmountFromCents(
                          invoiceDetailsDialog.invoice.tax,
                          invoiceDetailsDialog.invoice.currency,
                        )
                      }}
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colspan="3" class="text-right text-h6 font-weight-bold">
                      Gesamtbetrag
                    </td>
                    <td
                      class="text-right text-h6 font-weight-bold primary--text"
                    >
                      {{
                        getAmountFromCents(
                          invoiceDetailsDialog.invoice.total,
                          invoiceDetailsDialog.invoice.currency,
                        )
                      }}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Additional Info -->
          <v-card
            outlined
            v-if="
              invoiceDetailsDialog.invoice.description ||
              invoiceDetailsDialog.invoice.footer
            "
          >
            <v-card-subtitle class="font-weight-medium">
              <v-icon left small>mdi-note-text</v-icon>
              Zusätzliche Informationen
            </v-card-subtitle>
            <v-card-text>
              <div v-if="invoiceDetailsDialog.invoice.description" class="mb-2">
                <div class="caption grey--text">Beschreibung</div>
                <div>{{ invoiceDetailsDialog.invoice.description }}</div>
              </div>
              <div v-if="invoiceDetailsDialog.invoice.footer">
                <div class="caption grey--text">Fußnote</div>
                <div>{{ invoiceDetailsDialog.invoice.footer }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="invoiceDetailsDialog.invoice?.hostedInvoiceUrl"
            text
            color="primary"
            @click="openInvoice(invoiceDetailsDialog.invoice.hostedInvoiceUrl)"
          >
            <v-icon left small>mdi-open-in-new</v-icon>
            Rechnung öffnen
          </v-btn>
          <v-btn
            v-if="invoiceDetailsDialog.invoice?.invoicePdf"
            text
            color="primary"
            @click="openInvoice(invoiceDetailsDialog.invoice.invoicePdf)"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            PDF herunterladen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="invoiceDetailsDialog.model = false"
          >
            Schließen
          </v-btn>

          <!-- Line Item Licenses Dialog -->
          <v-dialog
            v-model="lineItemLicensesDialog.model"
            max-width="1200px"
            scrollable
          >
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon left color="primary">mdi-account-multiple</v-icon>
                <span>Zugeordnete Lizenzen</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="lineItemLicensesDialog.model = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text v-if="lineItemLicensesDialog.lineItem">
                <!-- Line Item Info -->
                <v-card outlined class="mb-4">
                  <v-card-subtitle class="font-weight-medium">
                    <v-icon left small>mdi-file-document</v-icon>
                    Rechnungsposition
                  </v-card-subtitle>
                  <v-card-text>
                    <div class="font-weight-medium mb-2">
                      {{
                        lineItemLicensesDialog.lineItem.product?.name ||
                        "Keine Beschreibung"
                      }}
                    </div>
                    <v-row dense>
                      <v-col cols="6" md="3">
                        <div class="caption grey--text">Rechnung</div>
                        <div class="font-weight-medium">
                          {{ invoiceDetailsDialog.invoice.number || "Entwurf" }}
                        </div>
                      </v-col>
                      <v-col cols="6" md="3">
                        <div class="caption grey--text">Zeitraum</div>
                        <div v-if="lineItemLicensesDialog.lineItem.period">
                          {{
                            new Date(
                              lineItemLicensesDialog.lineItem.period.start *
                                1000,
                            ).toLocaleDateString("de-DE")
                          }}
                          -
                          {{
                            new Date(
                              lineItemLicensesDialog.lineItem.period.end * 1000,
                            ).toLocaleDateString("de-DE")
                          }}
                        </div>
                        <div v-else class="grey--text">-</div>
                      </v-col>
                      <v-col cols="4" md="2">
                        <div class="caption grey--text">Menge</div>
                        <div>
                          {{ lineItemLicensesDialog.lineItem.quantity || 1 }}
                        </div>
                      </v-col>
                      <v-col cols="4" md="2">
                        <div class="caption grey--text">Einzelpreis</div>
                        <div>
                          {{
                            getAmountFromCents(
                              lineItemLicensesDialog.lineItem.amount /
                                (lineItemLicensesDialog.licenses.length || 1),
                              lineItemLicensesDialog.lineItem.currency ||
                                invoiceDetailsDialog.invoice.currency,
                            )
                          }}
                        </div>
                      </v-col>
                      <v-col cols="4" md="2">
                        <div class="caption grey--text">Summe</div>
                        <div class="font-weight-medium">
                          {{
                            getAmountFromCents(
                              lineItemLicensesDialog.lineItem.amount,
                              lineItemLicensesDialog.lineItem.currency ||
                                invoiceDetailsDialog.invoice.currency,
                            )
                          }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <!-- Licenses List -->
                <v-card outlined>
                  <v-card-subtitle class="font-weight-medium">
                    <v-icon left small>mdi-account-details</v-icon>
                    Zugeordnete Lizenzen ({{
                      lineItemLicensesDialog.licenses.length
                    }})
                  </v-card-subtitle>

                  <v-data-table
                    :items="lineItemLicensesDialog.licenses"
                    :headers="licenseDetailsHeaders"
                    :items-per-page="10"
                    class="elevation-0"
                    item-key="uniqueKey"
                  >
                    <template v-slot:[`item.userName`]="{ item }">
                      <div class="font-weight-medium">{{ item.userName }}</div>
                      <div class="caption grey--text">
                        {{ item.userEmail || item.userId }}
                      </div>
                    </template>

                    <template v-slot:[`item.licenseType`]="{ item }">
                      <v-chip
                        small
                        :color="getLicenseTypeColor(item.licenseType)"
                        outlined
                      >
                        {{ item.licenseType }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.prorationDetails`]="{ item }">
                      <div
                        v-if="item.sharedQuantity && item.sharedQuantity > 1"
                      >
                        <div class="font-weight-medium warning--text">
                          <v-icon x-small color="warning" class="mr-1"
                            >mdi-clock-outline</v-icon
                          >
                          Anteilig
                        </div>
                        <div class="caption grey--text">
                          1 von {{ item.sharedQuantity }} Nutzern
                        </div>
                      </div>
                      <div
                        v-else-if="item.prorationDetails?.prorationFactor < 1"
                        class="font-weight-medium warning--text"
                      >
                        <v-icon x-small color="warning" class="mr-1"
                          >mdi-clock-outline</v-icon
                        >
                        Anteilig
                      </div>
                      <div v-else class="success--text font-weight-medium">
                        <v-icon x-small color="success" class="mr-1"
                          >mdi-check-circle</v-icon
                        >
                        Vollzeitraum
                      </div>
                    </template>

                    <template v-slot:[`item.billedAmount`]="{ item }">
                      <div class="font-weight-medium">
                        {{
                          getAmountFromCents(item.billedAmount, item.currency)
                        }}
                      </div>
                      <div
                        v-if="item.sharedQuantity && item.sharedQuantity > 1"
                        class="caption grey--text"
                      >
                        von
                        {{ getAmountFromCents(item.unitAmount, item.currency) }}
                      </div>
                      <div
                        v-else-if="item.prorationDetails?.prorationFactor < 1"
                        class="caption grey--text"
                      >
                        von
                        {{ getAmountFromCents(item.unitAmount, item.currency) }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  text
                  @click="lineItemLicensesDialog.model = false"
                >
                  Schließen
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import InvoiceStatusChip from "@/components/admin/billing/InvoiceStatusChip.vue";
import { billingHelpersMixin } from "@/mixins/billingHelpers";

export default {
  name: "BillingTabInvoices",
  mixins: [billingHelpersMixin],
  components: {
    InvoiceStatusChip,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    invoices: {
      type: Array,
      default: () => [],
    },
    licenses: {
      type: Array,
      default: () => [],
    },
    hasWriteAccess: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      invoicesSearch: "",
      invoiceStatusFilter: ["open", "paid"],
      invoicesHeaders: [
        { text: "Rechnungsnummer", value: "number", width: "150" },
        { text: "Rechnungsdatum", value: "created", width: "120" },
        // { text: "Abrechnungszeitraum", value: "period", width: "180" },
        { text: "Betrag", value: "total", align: "right", width: "120" },
        {
          text: "Lizenzen",
          value: "totalLicenses",

          align: "center",
          width: "100",
        },
        { text: "Status", value: "status", width: "120" },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "right",
          width: "120",
        },
      ],
      licenseDetailsDialog: {
        model: false,
        invoice: null,
      },
      invoiceDetailsDialog: {
        model: false,
        invoice: null,
      },
      lineItemLicensesDialog: {
        model: false,
        lineItem: null,
        licenses: [],
      },
      licenseDetailsHeaders: [
        { text: "Nutzer", value: "userName", width: "200" },
        {
          text: "Rechnungspositionen/Lizenz",
          value: "licenseType",
          width: "150",
        },
        {
          text: "Anteilig",
          value: "prorationDetails",
          width: "200",
        },
        {
          text: "Betrag",
          value: "billedAmount",
          align: "right",
          width: "120",
        },
      ],
    };
  },
  computed: {
    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    filteredInvoices() {
      let filtered = this.invoices;

      // Apply search filter
      if (this.invoicesSearch) {
        const search = this.invoicesSearch.toLowerCase();
        filtered = filtered.filter((invoice) => {
          const number = (invoice.number || "").toLowerCase();
          const id = (invoice.id || "").toLowerCase();
          const description = (invoice.description || "").toLowerCase();
          const amount = this.getAmountFromCents(
            invoice.total,
            invoice.currency,
          ).toLowerCase();
          return (
            number.includes(search) ||
            id.includes(search) ||
            description.includes(search) ||
            amount.includes(search)
          );
        });
      }

      // Apply status filter
      if (this.invoiceStatusFilter && this.invoiceStatusFilter.length > 0) {
        filtered = filtered.filter((invoice) =>
          this.invoiceStatusFilter.includes(invoice.status),
        );
      }

      return filtered;
    },
    invoiceStatusOptions() {
      const statuses = [
        { text: "Offen", value: "open", icon: "mdi-cash-clock" },
        { text: "Bezahlt", value: "paid", icon: "mdi-cash-check" },
        { text: "Storniert", value: "void", icon: "mdi-cash-off" },
        {
          text: "Uneinbringlich",
          value: "uncollectible",
          icon: "mdi-cash-remove",
        },
      ];
      return statuses;
    },
    invoiceTotalAmount() {
      // Show customer balance instead of total invoice amount
      const currency = this.customer?.currency || "eur";
      return this.getAmountFromCents(
        Math.abs(this.customer?.balance || 0),
        currency,
      );
    },
    invoiceOpenAmount() {
      // Calculate from open invoices
      const total = this.invoices
        .filter((invoice) => invoice.status === "open")
        .reduce((sum, invoice) => {
          return sum + (invoice.amountRemaining || invoice.total || 0);
        }, 0);

      if (this.invoices.length > 0) {
        const currency = this.invoices[0]?.currency || "eur";
        return this.getAmountFromCents(total, currency);
      }
      return "0,00 EUR";
    },
    licenseBillingEntriesWithKeys() {
      if (!this.licenseDetailsDialog.invoice?.billedLicenses) return [];
      return this.licenseDetailsDialog.invoice.billedLicenses.map(
        (item, index) => ({
          ...item,
          uniqueKey: `${item.licenseId}-${index}`,
        }),
      );
    },
    groupedInvoiceLines() {
      if (!this.invoiceDetailsDialog.invoice?.lines) return [];

      const lines = this.invoiceDetailsDialog.invoice.lines;
      const grouped = {};

      // Group by metadata.type
      lines.forEach((line, index) => {
        const type = line.product?.metadata?.type || "Sonstige Positionen";
        if (!grouped[type]) {
          grouped[type] = {
            type: type,
            lines: [],
            subtotal: 0,
          };
        }
        grouped[type].lines.push({ ...line, originalIndex: index });
        grouped[type].subtotal += line.amount || 0;
      });

      // Sort groups: named types first (alphabetically), "Sonstige" last
      return Object.values(grouped).sort((a, b) => {
        if (a.type === "Sonstige") return 1;
        if (b.type === "Sonstige") return -1;
        return a.type.localeCompare(b.type);
      });
    },
  },
  methods: {
    openInvoice(url) {
      window.open(url, "_blank");
    },
    normalizeTimestampToSeconds(value) {
      if (!value) return null;

      if (typeof value === "number") {
        return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
      }

      if (value.seconds) return value.seconds;
      if (value._seconds) return value._seconds;
      if (typeof value.toDate === "function") {
        return Math.floor(value.toDate().getTime() / 1000);
      }

      return null;
    },
    licenseMatchesLineItem(license, lineItem) {
      if (!license || !lineItem) return false;

      // Prefer exact matching by Stripe line item id if available.
      if (license.lineItemId && lineItem.id) {
        return license.lineItemId === lineItem.id;
      }

      const productName = (lineItem.product?.name || "").trim();
      const licenseType = (license.licenseType || "").trim();
      const licenseMatches =
        productName.length > 0 &&
        licenseType.length > 0 &&
        licenseType === productName;

      if (!licenseMatches) return false;

      const lineItemPeriodStart = this.normalizeTimestampToSeconds(
        lineItem.period?.start,
      );
      const lineItemPeriodEnd = this.normalizeTimestampToSeconds(
        lineItem.period?.end,
      );
      const licensePeriodStart = this.normalizeTimestampToSeconds(
        license.periodStart,
      );
      const licensePeriodEnd = this.normalizeTimestampToSeconds(
        license.periodEnd,
      );

      if (
        lineItemPeriodStart &&
        lineItemPeriodEnd &&
        licensePeriodStart &&
        licensePeriodEnd
      ) {
        return (
          licensePeriodStart === lineItemPeriodStart &&
          licensePeriodEnd === lineItemPeriodEnd
        );
      }

      return true;
    },
    showInvoiceLicenseDetails(invoice) {
      // The invoice is already enriched with billedLicenses and licenseSummary from AdminBilling
      // Just pass it directly to the dialog
      this.licenseDetailsDialog.invoice = invoice;
      this.licenseDetailsDialog.model = true;
    },
    showInvoiceDetails(invoice) {
      this.invoiceDetailsDialog.invoice = invoice;
      this.invoiceDetailsDialog.model = true;
    },
    getLineItemLicenseCount(lineItem) {
      if (!this.invoiceDetailsDialog.invoice?.billedLicenses) return 0;
      return this.invoiceDetailsDialog.invoice.billedLicenses.filter(
        (license) => this.licenseMatchesLineItem(license, lineItem),
      ).length;
    },
    showLineItemLicenses(lineItem) {
      if (!this.invoiceDetailsDialog.invoice?.billedLicenses) return;

      const matchingLicenses = this.invoiceDetailsDialog.invoice.billedLicenses
        .map((license, index) => ({
          ...license,
          uniqueKey: `${license.licenseId}-${index}`,
        }))
        .filter((license) => this.licenseMatchesLineItem(license, lineItem));

      this.lineItemLicensesDialog.lineItem = lineItem;
      this.lineItemLicensesDialog.licenses = matchingLicenses;
      this.lineItemLicensesDialog.model = true;
    },
  },
};
</script>
